import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ContentCopy as ImportIcon,
  Save,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NovelImportDialog } from './novel-import-dialog';
import { SectionFields } from './section-fields';
import { EntitySettingsForm } from './types';

import { EntitySettingsType, useEntitySettingsManager } from '@/features/shared/hooks/use-entity-settings-manager';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';

interface EntitySettingsViewProps {
  type: EntitySettingsType;
  title: string;
  breadcrumbLabel: string;
  breadcrumbLink: string;
}

export function EntitySettingsView({ type, title, breadcrumbLabel, breadcrumbLink }: EntitySettingsViewProps) {
  const { t } = useTranslation();
  const { selectedNovelId: novelId } = useNovelRepository();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const { toast } = useToast();

  const methods = useForm<EntitySettingsForm>({
    defaultValues: {
      advancedSections: [],
    },
  });

  const { fields: sections, append: appendSection, remove: removeSection } = useFieldArray({
    control: methods.control,
    name: 'advancedSections',
  });

  const {
    isLoading,
    isSaving,
    loadSettings,
    saveSettings
  } = useEntitySettingsManager(type);

  useEffect(() => {
    if (novelId) {
      loadSettings(novelId, (data) => methods.reset(data));
    }
  }, [novelId, loadSettings, methods]);

  const onImport = async (sourceNovelId: string) => {
    setImportDialogOpen(false);
    await loadSettings(sourceNovelId, (data) => {
      methods.reset(data);
      toast({
        title: t('entity_settings.schema_imported'),
        description: t('entity_settings.schema_imported_desc'),
      });
    });
  };

  const hasInvalidFieldLabels = () => {
    const invalidChars = ['~', '*', '/', '[', ']'];
    const allSections = methods.watch('advancedSections') || [];

    return allSections.some(section =>
      section.fields?.some(field =>
        invalidChars.some(char => field.label?.includes(char))
      )
    );
  };

  const onSubmit = async (values: EntitySettingsForm) => {
    await saveSettings(values);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: 2 }}>
        <CircularProgress color="primary" thickness={3} size={32} />
        <Typography variant="overline" color="text.secondary">
          {t('entity_settings.initializing_schema')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 6 }}>
              <Box>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  sx={{ mb: 1, '& .MuiBreadcrumbs-ol': { color: 'text.secondary' } }}
                  separator={<Typography variant="caption" sx={{ mx: 0.5, color: 'text.disabled' }}>/</Typography>}
                >
                  <MuiLink
                    component={Link}
                    to={breadcrumbLink}
                    underline="hover"
                    color="inherit"
                    sx={{ fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer' }}
                  >
                    {breadcrumbLabel}
                  </MuiLink>
                  <Typography color="text.primary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                    {t('entity_settings.settings')}
                  </Typography>
                </Breadcrumbs>
                <Typography variant="h1">
                  {title}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<ImportIcon sx={{ fontSize: '1rem' }} />}
                  onClick={() => setImportDialogOpen(true)}
                  sx={{ color: 'text.secondary', borderColor: 'divider' }}
                >
                  {t('entity_settings.import')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSaving || hasInvalidFieldLabels()}
                  startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: '1rem' }} />}
                >
                  {t('entity_settings.save_config')}
                </Button>
              </Stack>
            </Stack>

            <Stack spacing={3}>
              {sections.map((section, sectionIndex) => (
                <Card key={section.id} variant="outlined" sx={{ borderRadius: 2, position: 'relative' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                      <DragIcon sx={{ color: 'text.secondary', opacity: 0.3, fontSize: '1.25rem' }} />
                      <TextField
                        {...methods.register(`advancedSections.${sectionIndex}.title`)}
                        placeholder={t('entity_settings.section_title')}
                        variant="standard"
                        fullWidth
                        InputProps={{
                          sx: { fontSize: '1.1rem', fontWeight: 700, p: 0 },
                          disableUnderline: false
                        }}
                      />
                      <IconButton onClick={() => removeSection(sectionIndex)} color="error" size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <SectionFields sectionIndex={sectionIndex} />
                  </CardContent>
                </Card>
              ))}

              <Button
                variant="outlined"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => appendSection({ id: crypto.randomUUID(), title: '', order: sections.length, fields: [] })}
                sx={{
                  py: 2,
                  borderStyle: 'dashed',
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  letterSpacing: '0.1em'
                }}
              >
                {t('entity_settings.add_custom_section')}
              </Button>
            </Stack>
          </form>
        </FormProvider>
      </Container>

      <NovelImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={onImport}
      />
    </Box>
  );
}
